import { Component, OnInit, OnDestroy } from '@angular/core';
import { AppModule } from '../../app.module';
import { MapSelect } from '../../map/map-select/map-select';
import { IncidentTyreComponent } from '../incident/incident-tyre/incident-tyre';
import { Document, Incident, IncidentLocation, IncidentTyre, IncidentCloseRequest, IncidentFieldFlags } from '../../models/incident';
import { IncidentType, TyreLocation } from '../../shared/enums';
import { IncidentService } from '../../shared/incident.service';
import { NotificationService } from '../../shared/notification.service';
import { SharedDataService } from '../../shared/shareddata.service';
import { AuthService } from '../../auth.service';
import { ClientService } from '../../shared/client.service';
import { Router } from '@angular/router';
import { Driver, Vehicle, FleetClient, ClientLink, IncidentClientLink, SavedLocation } from '../../models/client';
import { PriceTemplate } from '../../models/pricing';
import { QuoteService } from '../../shared/quote.service';
import { FileUploader } from '../../shared/file-uploader/file-uploader';
import { IncidentDocumentTile } from '../incident-document-tile/incident-document-tile';
import { WhatsappService } from '../../whatsapp/whatsapp.service';
import { Subscription } from 'rxjs';
import { WhatsappMessage } from '../../models/whatsapp';
import { SocketMessageType } from '../../models/websocket';
import { WhatsappFieldDirective } from '../../shared/whatsapp-field.directive';
import { isIncidentDocument } from '../../shared/media';
import { messageDocument } from '../../whatsapp/whatsapp-media';

@Component({
  selector: 'app-incident-datacapture',
  imports: [AppModule, MapSelect, IncidentTyreComponent, FileUploader, IncidentDocumentTile, WhatsappFieldDirective],
  templateUrl: './incident-datacapture.html',
  styleUrl: './incident-datacapture.less'
})
export class IncidentDatacapture implements OnInit, OnDestroy {

  public incident: Incident = new Incident();
  public get visibleDocuments() { return this.incident.documents.filter(isIncidentDocument); }
  public savedLocation: SavedLocation = new SavedLocation();
  public incidentType = IncidentType;
  public tyreLocation = TyreLocation;
  public isLoading: boolean = false;
  public isLoadingTruckDrivers: boolean = false;
  public isLoadingFleetVehicles: boolean = false;
  public isSavedLocationNameModalVisible = false;
  public isAddDriverModalVisible = false;
  public isAddVehicleModalVisible = false;
  public isAddFleetModalVisible = false;
  public isUploadModalVisible = false;
  public whatsappMessageIds: number[] = [];

  public truckDrivers: Driver[] = [];
  public fleetVehicles: Vehicle[] = [];
  public fleetClient: FleetClient = new FleetClient();
  public selectedPricing: PriceTemplate = new PriceTemplate();
  public selectedSaveLocationId: string = '';
  public selectedServiceProviderId: number | null = null;
  public showWhatsappNotification: boolean = false;
  public fieldsFromWhatsapp: IncidentFieldFlags = new IncidentFieldFlags();

  private geocoder = new google.maps.Geocoder();
  private whatsappMessageListener: Subscription;

  constructor(
    public incidentService: IncidentService,
    private notification: NotificationService,
    public sharedDataService: SharedDataService,
    public authService: AuthService,
    private clientService: ClientService,
    private router: Router,
    private quoteService: QuoteService,
    private whatsappService: WhatsappService
  ) {}



  ngOnInit(): void {
    this.sharedDataService.isOnDataCaptureMode = true; //to let WS know what to do with incoming messages
    this.whatsappMessageListener = this.whatsappService.$incomingWhatsappMessages.subscribe((msg) => {
      console.log("Received WA message in datacapture:", msg);
      // Linked messages belong to their existing breakdown, not this new capture.
      if (msg.incidentId || this.whatsappMessageIds.includes(msg.whatsappMessageId)) return;
      this.whatsappMessageIds.push(msg.whatsappMessageId);
      this.mergeIncomingMessage(msg);
    });
    this.initializeIncident();
    
  }

  ngOnDestroy(): void {
    // Cleanup if needed
    this.sharedDataService.isOnDataCaptureMode = false; //make sure default WS behaviour for WA is back
    if (this.whatsappMessageListener) {
      this.whatsappMessageListener.unsubscribe();
    }
  }

  private mergeIncomingMessage(waMessage: WhatsappMessage): void {
    // Logic to merge the incoming WhatsApp message into the current incident
    this.showWhatsappNotification = true;
    
    if (waMessage.messageType === SocketMessageType.WhatsappIncident) {
      // New message for the current incident
      this.mergeIncomingIncident(JSON.parse(waMessage.extendedObject!) as Incident);
      console.log("Merging WhatsApp incident message into current incident:", waMessage);
    }
    else if (waMessage.messageType === SocketMessageType.IncidentLocationUpdate) {
      // Location update for the current incident
      const locationUpdate = JSON.parse(waMessage.extendedObject!) as IncidentLocation;
      if (locationUpdate) {
        let newLocation = new IncidentLocation();
        newLocation.latitude = locationUpdate.latitude;
        newLocation.longitude = locationUpdate.longitude;
        newLocation.description = locationUpdate.description;
        newLocation.descriptionShort = locationUpdate.descriptionShort;
        this.incident.location = newLocation;
        this.geocodeLocation(new google.maps.LatLng(this.incident.location.latitude, this.incident.location.longitude));

        console.log("Updated incident location from WhatsApp message:", this.incident.location);
      }
    }
    // Caption enrichment can accompany an attachment on any WhatsApp event.
    const document = messageDocument(waMessage);
    if (document) {
      const existing = this.incident.documents.find(doc => doc.documentId === document.documentId);
      if (existing) Object.assign(existing, document);
      else this.incident.documents.push(document);
    }
    
  }

  private mergeIncomingIncident(incomingIncident: Incident): void {
    console.log("Going to merge", this.incident);
    


    // Merge basic fields
    if (!this.incident.clientId && incomingIncident.clientId) {
      this.incident.clientId = incomingIncident.clientId;
      this.fieldsFromWhatsapp.clientId = true;
    }
    if (!this.incident.clientName && incomingIncident.clientName) {
      this.incident.clientName = incomingIncident.clientName;
      this.fieldsFromWhatsapp.clientName = true;
    }
    if (!this.incident.incidentDescription && incomingIncident.incidentDescription) {
      this.incident.incidentDescription = incomingIncident.incidentDescription;
      this.fieldsFromWhatsapp.incidentDescription = true;
    }
    
    this.incident.incidentType = incomingIncident.incidentType;
    this.fieldsFromWhatsapp.incidentType = true;
    
    if (!this.incident.fleetVehicleOdoReading && incomingIncident.fleetVehicleOdoReading) {
      this.incident.fleetVehicleOdoReading = incomingIncident.fleetVehicleOdoReading;
      this.fieldsFromWhatsapp.fleetVehicleOdoReading = true;
    }

    // Merge driver information
    if (incomingIncident.driver) {
      if (!this.incident.driver.driverId && incomingIncident.driver.driverId) {
        this.incident.driver.driverId = incomingIncident.driver.driverId;
        this.fieldsFromWhatsapp.driver = true;
      }
      if (!this.incident.driver.name && incomingIncident.driver.name) {
        this.incident.driver.name = incomingIncident.driver.name;
        this.fieldsFromWhatsapp.driver = true;
      }
      if (!this.incident.driver.phoneNumber && incomingIncident.driver.phoneNumber) {
        this.incident.driver.phoneNumber = incomingIncident.driver.phoneNumber;
        this.fieldsFromWhatsapp.driver = true;
      }
    }

    // Merge fleet vehicle information
    if (incomingIncident.fleetVehicle) {
      if (!this.incident.fleetVehicle.vehicleId && incomingIncident.fleetVehicle.vehicleId) {
        this.incident.fleetVehicle.vehicleId = incomingIncident.fleetVehicle.vehicleId;
        this.fieldsFromWhatsapp.fleetVehicle = true;
      }
      if (!this.incident.fleetVehicle.registrationNumber && incomingIncident.fleetVehicle.registrationNumber) {
        this.incident.fleetVehicle.registrationNumber = incomingIncident.fleetVehicle.registrationNumber;
        this.fieldsFromWhatsapp.fleetVehicle = true;
      }
      if (!this.incident.fleetVehicle.vinNumber && incomingIncident.fleetVehicle.vinNumber) {
        this.incident.fleetVehicle.vinNumber = incomingIncident.fleetVehicle.vinNumber;
        this.fieldsFromWhatsapp.fleetVehicle = true;
      }
    }

    // Merge location information
    if (incomingIncident.location) {
      if (!this.incident.location.latitude && incomingIncident.location.latitude) {
        this.incident.location.latitude = incomingIncident.location.latitude;
        this.fieldsFromWhatsapp.location = true;
      }
      if (!this.incident.location.longitude && incomingIncident.location.longitude) {
        this.incident.location.longitude = incomingIncident.location.longitude;
        this.fieldsFromWhatsapp.location = true;
      }
      if (!this.incident.location.description && incomingIncident.location.description) {
        this.incident.location.description = incomingIncident.location.description;
      }
      if (!this.incident.location.descriptionShort && incomingIncident.location.descriptionShort) {
        this.incident.location.descriptionShort = incomingIncident.location.descriptionShort;
      }
    }

    // Merge tyre positions if current is empty or default
    if (incomingIncident.tyrePositions && incomingIncident.tyrePositions.length > 0 && incomingIncident.tyrePositions[0].tyreLocation && incomingIncident.tyrePositions[0].tyreNumber && incomingIncident.tyrePositions[0].size) {
      const hasOnlyDefaultTyre = this.incident.tyrePositions.length === 1 && 
                                 this.incident.tyrePositions[0].tyreLocation === TyreLocation.trailer &&
                                 this.incident.tyrePositions[0].tyreNumber === 0;
      if (this.incident.tyrePositions.length === 0 || hasOnlyDefaultTyre) {
        this.incident.tyrePositions = incomingIncident.tyrePositions;
        this.fieldsFromWhatsapp.tyrePositions = true;
      }
    }

    // Merge tyre brands if current has only default value
    if (incomingIncident.tyreBrands && incomingIncident.tyreBrands.length > 0) {
      const hasOnlyDefaultBrand = this.incident.tyreBrands.length === 1 && this.incident.tyreBrands[0] === -1;
      if (hasOnlyDefaultBrand) {
        this.incident.tyreBrands = incomingIncident.tyreBrands;
        this.fieldsFromWhatsapp.tyreBrands = true;
      }
    }


    console.log("Merged incoming incident into current incident:", this.incident);
  }

  private initializeIncident(): void {
    this.showWhatsappNotification = false;
    this.whatsappMessageIds = [];
    this.incident = new Incident();
    this.incident.statusId = 0;
    this.incident.tyrePositions = [];
    this.incident!.tyrePositions.push(new IncidentTyre(TyreLocation.trailer, 0, '315225'));
    this.incident.tyreBrands = [-1]; // Default to Import
    this.incident.incidentOperators = [{
      operatorId: this.authService.user?.userId || 0,
      name: `${this.authService.user?.firstName || ''} ${this.authService.user?.lastName || ''}`,
      clientId: this.authService.user?.clientId || 0,
      cellNumber: this.authService.user?.userCell || '',
      userType: this.authService.user?.userType || 2,
      isActive: true
    }];
    this.incident.incidentClients = [];
    
    // Add current client from authService
    if (this.authService.user?.clientId) {
      const currentClientLink = this.sharedDataService.clientLinks.find(
      link => link.clientId === this.authService.user?.clientId
      );
      if (currentClientLink) {
      this.incident.incidentClients.push(new IncidentClientLink(currentClientLink));
      }
    }
    this.incident.driver = new Driver();
    this.incident.fleetVehicle = new Vehicle();
    this.incident.location = new IncidentLocation();
    this.incident.incidentDescription = '';
    this.incident.fleetVehicleOdoReading = 0;
    this.incident.documents = [];
  }

  // Fleet Management
  public onSelectFleet(link: ClientLink): void {
    this.incident.clientId = link.clientId;
    this.incident.clientName = link.clientName;
    this.getFleetTruckDrivers();
    this.getFleetVehicles();
  }

  private getFleetTruckDrivers(): void {
    if (this.incident.clientId && this.incident.clientId > 0) {
      this.isLoadingTruckDrivers = true;
      this.clientService.fleetTruckDrivers(this.incident.clientId).subscribe({
        next: (drivers: Driver[]) => {
          this.truckDrivers = drivers;
          this.isLoadingTruckDrivers = false;
        },
        error: (error: any) => {
          this.notification.handleError(error, 'Failed to load drivers');
          this.isLoadingTruckDrivers = false;
        }
      });
    }
  }

  private getFleetVehicles(): void {
    if (this.incident.clientId && this.incident.clientId > 0) {
      this.isLoadingFleetVehicles = true;
      this.clientService.fleetVehicles(this.incident.clientId).subscribe({
        next: (vehicles: Vehicle[]) => {
          this.fleetVehicles = vehicles;
          this.isLoadingFleetVehicles = false;
        },
        error: (error: any) => {
          this.notification.handleError(error, 'Failed to load vehicles');
          this.isLoadingFleetVehicles = false;
        }
      });
    }
  }

  // Driver Management
  public onDriverChanged(driverId: number): void {
    this.incident.driver.driverId = driverId;
    const selectedDriver = this.truckDrivers.find(d => d.driverId === driverId);
    if (selectedDriver) {
      this.incident.driver.name = selectedDriver.name;
      this.incident.driver.phoneNumber = selectedDriver.phoneNumber;
    }
  }

  public onAddNewDriver(): void {
    this.incident.driver = new Driver();
    this.incident.driver.driverId = 0;
    this.isAddDriverModalVisible = true;
  }

  public onConfirmAddDriver(): void {
    if (!this.incident.driver.name || !this.incident.driver.phoneNumber) {
      this.notification.showError('Please enter driver name and phone number');
      return;
    }

    this.isLoading = true;
    this.clientService.addFleetTruckDriver(this.incident.clientId, this.incident.driver).subscribe({
      next: (driver: Driver) => {
        this.incident.driver = driver;
        this.truckDrivers.push(driver);
        this.notification.showSuccess(`Driver ${driver.name} added successfully`);
        this.isAddDriverModalVisible = false;
        this.isLoading = false;
      },
      error: (error: any) => {
        this.notification.handleError(error, 'Failed to add driver');
        this.isLoading = false;
      }
    });
  }

  // Vehicle Management
  public onVehicleChanged(vehicleId: number): void {
    this.incident.fleetVehicle.vehicleId = vehicleId;
    const selectedVehicle = this.fleetVehicles.find(v => v.vehicleId === vehicleId);
    if (selectedVehicle) {
      this.incident.fleetVehicle.registrationNumber = selectedVehicle.registrationNumber;
      this.incident.fleetVehicle.vinNumber = selectedVehicle.vinNumber;
    }
  }

  public onAddNewVehicle(): void {
    this.incident.fleetVehicle = new Vehicle();
    this.incident.fleetVehicle.vehicleId = 0;
    this.isAddVehicleModalVisible = true;
  }

  public onConfirmAddVehicle(): void {
    if (!this.incident.fleetVehicle.registrationNumber) {
      this.notification.showError('Please enter vehicle registration number');
      return;
    }

    this.isLoading = true;
    this.clientService.addFleetVehicle(this.incident.clientId, this.incident.fleetVehicle).subscribe({
      next: (vehicle: Vehicle) => {
        this.incident.fleetVehicle = vehicle;
        this.fleetVehicles.push(vehicle);
        this.notification.showSuccess(`Vehicle ${vehicle.registrationNumber} added successfully`);
        this.isAddVehicleModalVisible = false;
        this.isLoading = false;
      },
      error: (error: any) => {
        this.notification.handleError(error, 'Failed to add vehicle');
        this.isLoading = false;
      }
    });
  }

  // Fleet Management
  public onAddNewFleet(): void {
    this.fleetClient = new FleetClient();
    this.selectedPricing = new PriceTemplate();
    this.isAddFleetModalVisible = true;
  }

  public onSelectFleetPricing(pricing: PriceTemplate): void {
    this.selectedPricing = pricing;
    this.fleetClient.pricingId = pricing.pricingTemplateId;
  }

  public onConfirmAddFleet(): void {
    if (!this.fleetClient.clientName || !this.fleetClient.operatorName || 
        !this.fleetClient.operatorCell || !this.fleetClient.pricingId) {
      this.notification.showError('Please fill in all required fields');
      return;
    }

    this.isLoading = true;
    const currentClientId = this.authService.user?.clientId || 0;
    this.clientService.upsertFleet(currentClientId, this.fleetClient).subscribe({
      next: (client: FleetClient) => {
        this.incident.clientId = client.clientId;
        this.incident.clientName = client.clientName;
        this.sharedDataService.getFleets.push(client);
        this.notification.showSuccess(`Fleet ${client.clientName} added successfully`);
        this.isAddFleetModalVisible = false;
        this.isLoading = false;
        this.getFleetTruckDrivers();
        this.getFleetVehicles();
      },
      error: (error: any) => {
        this.notification.handleError(error, 'Failed to add fleet');
        this.isLoading = false;
      }
    });
  }

  // Incident Type Selection
  public onSelectIncidentType(type: IncidentType): void {
    this.incident.incidentType = type;
  }

  // Tyre Position Management
  public getTyresInPosition(loc: TyreLocation): any[] {
    return this.incident.tyrePositions.filter(pos => pos.tyreLocation === loc);
  }

  public onClickTyreLocation(tyreLocation: TyreLocation): void {
    const existingIndex = this.incident.tyrePositions.findIndex(pos => pos.tyreLocation === tyreLocation);

    if (existingIndex !== -1) {
      this.incident.tyrePositions.splice(existingIndex, 1);
    } else {
      const newTyre = {
        tyreLocation: tyreLocation,
        tyreNumber: 0,
        size: '',
        sizeDescription: '',
        qty: 1
      };
      this.incident.tyrePositions.push(newTyre);
    }
  }

  public hasTyreInLocation(tyreLocation: TyreLocation): boolean {
    return this.incident.tyrePositions.some(pos => pos.tyreLocation === tyreLocation);
  }

  // Tyre Type/Brand Selection
  public onSelectTyreType(brandId: number, brandName: string = ""): void {
    if (brandId === -1 || brandId === -2 || brandId === -3) {
      this.incident.tyreBrands = [brandId];
    }
  }

  public isTyreTypeSelected(brandId: number): boolean {
    return this.incident.tyreBrands.includes(brandId);
  }

  // Location Management
  public onMapSelected(location: IncidentLocation | null): void {
    if (location) {
      this.incident.location = location;
      const latLng = new google.maps.LatLng(location.latitude, location.longitude);
      this.geocodeLocation(latLng);
    }
  }

  private geocodeLocation(latLng: google.maps.LatLng): void {
    this.geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        this.incident.location.description = results[0].formatted_address;
        const addressComponents = results[0].address_components;
        const locality = addressComponents.find(comp => comp.types.includes('locality'));
        this.incident.location.descriptionShort = locality ? locality.long_name : results[0].formatted_address;
      } else {
        this.incident.location.description = `${this.incident.location.latitude}, ${this.incident.location.longitude}`;
        this.incident.location.descriptionShort = this.incident.location.description;
      }
    });
  }

  public onSavedLocationModelChange(value: any): void {
    const savedLocation = this.sharedDataService.savedLocations.find(loc => loc.savedLocationId === value);
    if (savedLocation) {
      this.incident.location = new IncidentLocation();
      this.incident.location.latitude = savedLocation.latitude;
      this.incident.location.longitude = savedLocation.longitude;
      this.incident.location.description = savedLocation.description;
      this.incident.location.descriptionShort = savedLocation.description;
    }
  }

  public onClickSaveLocation(): void {
    if (!this.savedLocation.description) {
      this.notification.showError('Please enter a description for the location');
      return;
    }

    this.savedLocation.latitude = this.incident.location.latitude;
    this.savedLocation.longitude = this.incident.location.longitude;
    this.savedLocation.clientId = this.authService.user?.clientId || 0;

    this.clientService.upsertSavedLocation(this.savedLocation).subscribe({
      next: (location: SavedLocation) => {
        this.sharedDataService.savedLocations.push(location);
        this.notification.showSuccess('Location saved successfully');
        this.isSavedLocationNameModalVisible = false;
        this.savedLocation = new SavedLocation();
      },
      error: (error: any) => {
        this.notification.handleError(error, 'Failed to save location');
      }
    });
  }

  // Form Validation
  public isFormValid(): boolean {
    if (!this.incident.clientId || (!this.incident.fleetVehicle.vehicleId && !this.incident.fleetVehicle.registrationNumber) || (!this.incident.driver.driverId && !this.incident.driver.phoneNumber)) {
      return false;
    }
    if (!this.incident.location || !this.incident.location.latitude || !this.incident.location.longitude) {
      return false;
    }
    if (this.incident.incidentType === IncidentType.Tyre && this.incident.tyrePositions.length === 0) {
      return false;
    }
    if (this.incident.incidentType === IncidentType.Tyre && this.incident.tyreBrands.length === 0) {
      return false;
    }
    // Check if service provider is selected
    if (!this.getSelectedServiceProvider()) {
      return false;
    }
    return true;
  }

  // Save Incident
  public onSaveIncident(): void {
    if (!this.isFormValid()) {
      this.notification.showError('Please fill in all required fields');
      return;
    }
    
    

    this.isLoading = true;
    
    // Clean up tyre positions
    this.incident.tyrePositions = this.incident.tyrePositions.filter(pos => pos.size !== undefined);
    
    // Add current user as operator if authenticated
    if (this.authService.user && !this.incident.incidentOperators.some(op => op.operatorId === this.authService.user?.userId)) {
      this.incident.incidentOperators.push({
        operatorId: this.authService.user?.userId || 0,
        name: `${this.authService.user?.firstName || ''} ${this.authService.user?.lastName || ''}`,
        clientId: this.authService.user?.clientId || 0,
        cellNumber: this.authService.user?.userCell || '',
        userType: this.authService.user?.userType || 2,
        isActive: true
      });
    }
    
    // Add client link if authenticated
    if (this.authService.user && this.authService.user.clientId && 
        !this.incident.incidentClients.some(link => link.clientId === this.authService.user?.clientId)) {
      const clientLink = this.sharedDataService.clientLinks.find(link => link.clientId === this.authService.user?.clientId);
      if (clientLink) {
        this.incident.incidentClients.push(new IncidentClientLink(clientLink));
      }
    }
    
    // Set initial status
    if (this.incident.statusId === 0) {
      this.incident.statusId = 1;
    }

    //set all client links to active
    this.incident.incidentClients.forEach(link => link.isActive = true);

    // console.log(this.incident);
    // return;

    this.incidentService.dataCaptureIncident(this.incident).subscribe({
      next: (updatedIncident: Incident) => {
        this.incident = updatedIncident;
        this.notification.showSuccess(`Breakdown #${updatedIncident.incidentNumber} created successfully`);
        // assign wa messages
        this.whatsappMessageIds.forEach(id => {
          this.incidentService.assignWhatsappMessageToIncident(updatedIncident.incidentId, id).subscribe({
            next: () => {
              console.log(`WhatsApp message ${id} assigned to incident ${updatedIncident.incidentId}`);
              //remove from list
              this.whatsappService.changeMessageIncident(id, updatedIncident.incidentId!, updatedIncident.incidentNumber!);
            },
            error: (error: any) => {
              console.error(`Failed to assign WhatsApp message ${id} to incident ${updatedIncident.incidentId}:`, error);
            }
          });
        });

        // Run service provider checks for the linked service provider
        const serviceProvider = this.getSelectedServiceProvider();
        if (serviceProvider) {
          console.log("GOing to run nao", serviceProvider);
          
          this.quoteService.runServiceProviderChecks(
            updatedIncident.incidentId,
            serviceProvider.clientId,
            serviceProvider
          ).subscribe({
            next: (updatedLink: IncidentClientLink) => {
              // Update the service provider link with the checks result
              this.notification.showSuccess(`Quote completed for ${serviceProvider.clientName}`);
            },
            error: (error: any) => {
              // Log error but don't block the flow
              console.error('Failed to run service provider checks:', error);
            }
          });
        }
        
        this.isLoading = false;
        
        // clear the incident so that the next incident can be created
        this.initializeIncident();
      },
      error: (error: any) => {
        this.notification.handleError(error, 'Failed to save breakdown');
        this.isLoading = false;
      }
    });
  }

  // Formatter for ODO input
  public formatterKm = (value: number): string => `${value} km`;
  public parserKm = (value: string): number => parseFloat(value.replace(' km', '')) || 0;

  // Service Provider Selection
  public getServiceProviders(): ClientLink[] {
    return this.sharedDataService.clientLinks.filter(link => link.connectionTypeId === 3);
  }

  public onServiceProviderSelected(serviceProviderId: number | null): void {
    if (!serviceProviderId) {
      return;
    }

    const serviceProvider = this.sharedDataService.clientLinks.find(
      link => link.clientId === serviceProviderId && link.connectionTypeId === 3
    );
    
    if (serviceProvider) {
      // Remove any existing service provider (only one allowed)
      this.incident.incidentClients = this.incident.incidentClients.filter(
        link => link.connectionTypeId !== 3
      );
      
      // Add the new service provider
      const incidentLink = new IncidentClientLink(serviceProvider);
      this.incident.incidentClients.push(incidentLink);
    }
  }

  public getSelectedServiceProvider(): IncidentClientLink | undefined {
    return this.incident.incidentClients.find(link => link.connectionTypeId === 3);
  }

  // File Upload Methods
  public handleUploadOk(): void {
    this.isUploadModalVisible = false;
  }

  public onUploadComplete(event: Document[]): void {
    
    // Merge the returned documents into the incident.documents array
    if (event && Array.isArray(event)) {
      if (!this.incident.documents) {
        this.incident.documents = []; //init array
      }
      // Only add documents that don't already exist in the array
      const newDocuments = event.filter((newDoc) => 
        !this.incident.documents.some((existingDoc) => 
          existingDoc.documentId === newDoc.documentId
        )
      );
      this.incident.documents = [...this.incident.documents, ...newDocuments];
      this.notification.showSuccess('Files uploaded successfully');
    }
  }

  public onClickAssignWorkReport(documentId: number): void { 
    //update the documentClassification to 2 for deocumetnId in incident.documents
    const document = this.incident.documents.find(doc => doc.documentId === documentId);
    if (document) {
      document.documentClassificationId = 2; // 2 = Work Report
      this.incident.statusId = 11; // Complete, so that the work report button disappears
    }
  }
}
